import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import "./InitialData.css";
import Select from "react-select";
import {
  getSketchDesignProjectById,
  getSketchDesignBlocks,
  getSketchDesignProjectCategories,
  addSketchDesignProjectCategory,
  updateSketchDesignBlock,
  saveSketchDesignBlocks,
  updateSketchDesignProject,
  deleteSketchDesignBlock,
  getSketchDesignSpots,
  createSketchDesignSpot,
  updateSketchDesignSpot,
  deleteSketchDesignSpot,
} from "../../../../api/projectApi";
import { selectStyles } from "../../../../utils/selectStyles";
import Preloader from "../../../Preloader/Preloader";
import VectorIcon from "../../../../images/projects/Vector.svg";
import PyramidIcon from "../../../../images/projects/Verified Badge.png";
import BuildingIcon from "../../../../images/projects/Condo.png";
import CubesIcon from "../../../../images/projects/Sugar Cubes.png";
import TrashIcon from "../../../../images/trash.svg";
import AddBlockModal from "./AddBlockModal";

const CREATE_NEW_CATEGORY_OPTION = {
  value: "__create_new_category__",
  label: "Создать категорию",
};

// Компонент для inline редактирования числовых значений
const InlineEditableCell = forwardRef(
  (
    {
      value,
      onSave,
      width = 100,
      step = "0.01",
      min = "0",
      isEditable: cellEditable = true,
      onTabNext,
    },
    ref,
  ) => {
    const [isEditing, setIsEditing] = useState(false);
    const [localValue, setLocalValue] = useState(value?.toString() || "0");
    const inputRef = useRef(null);
    const originalValueRef = useRef(value);

    useImperativeHandle(ref, () => ({
      startEditing: () => {
        if (cellEditable) {
          setIsEditing(true);
        }
      },
    }));

    useEffect(() => {
      setLocalValue(value?.toString() || "0");
    }, [value]);

    // Сохраняем исходное значение при входе в режим редактирования
    useEffect(() => {
      if (isEditing) {
        originalValueRef.current = value;
      }
    }, [isEditing, value]);

    useEffect(() => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus();
        const input = inputRef.current;
        requestAnimationFrame(() => input.select());
      }
    }, [isEditing]);

    const handleSave = () => {
      if (!cellEditable) return;
      const numValue = parseFloat(localValue);
      const finalValue = isNaN(numValue) ? 0 : Math.max(0, numValue);
      onSave(finalValue);
      setIsEditing(false);
    };

    const handleBlur = () => {
      if (!cellEditable) return;

      // Проверяем, изменилось ли значение от исходного
      const originalStr = originalValueRef.current?.toString() || "0";
      const currentStr = localValue;

      // Если значение не изменилось — просто выходим из режима редактирования
      if (originalStr === currentStr) {
        setIsEditing(false);
        return;
      }

      // Если значение пустое или некорректное — восстанавливаем исходное
      const numValue = parseFloat(localValue);
      if (localValue === "" || isNaN(numValue)) {
        setLocalValue(originalStr);
        setIsEditing(false);
        return;
      }

      // Сохраняем только если значение валидно и изменилось
      handleSave();
    };

    if (isEditing && cellEditable) {
      return (
        <input
          ref={inputRef}
          type="number"
          step={step}
          min={min}
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSave();
            } else if (e.key === "Escape") {
              setLocalValue(value?.toString() || "0");
              setIsEditing(false);
            } else if (e.key === "Tab" && !e.shiftKey && onTabNext) {
              e.preventDefault();
              handleSave();
              onTabNext();
            }
          }}
          className="sketch-inline-input"
          style={{ width: `${width}px` }}
        />
      );
    }

    const displayValue = value ?? 0;
    return (
      <span
        className="sketch-inline-editable"
        onClick={() => cellEditable && setIsEditing(true)}
        style={{
          width: `${width}px`,
          cursor: cellEditable ? "pointer" : "default",
        }}
      >
        {displayValue}
      </span>
    );
  },
);

// Компонент строки блока с поддержкой Tab-навигации и drag-and-drop
const BlockRow = forwardRef(
  (
    {
      block,
      blockIndex,
      spotName,
      spotId,
      spots = [],
      isEditable,
      onUpdateBlock,
      onDeleteBlock,
      onTabToNextRow,
      onDragStart,
      onDragOver,
      onDrop,
      onDragEnd,
      isDragging,
    },
    ref,
  ) => {
    const areaRef = useRef(null);
    const rateRef = useRef(null);
    const coeffRef = useRef(null);
    const spotDropdownRef = useRef(null);
    const [spotDropdownOpen, setSpotDropdownOpen] = useState(false);

    // Закрытие дропдауна по клику снаружи
    useEffect(() => {
      if (!spotDropdownOpen) return;
      const handleClickOutside = (e) => {
        if (
          spotDropdownRef.current &&
          !spotDropdownRef.current.contains(e.target)
        ) {
          setSpotDropdownOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [spotDropdownOpen]);

    useImperativeHandle(ref, () => ({
      startEditingArea: () => areaRef.current?.startEditing(),
    }));

    const handleSelectSpot = (selectedSpot) => {
      if (selectedSpot === null) {
        onUpdateBlock(block.id, { spot_id: 0, spot: null });
      } else {
        onUpdateBlock(block.id, { spot_id: selectedSpot.id, spot: selectedSpot });
      }
      setSpotDropdownOpen(false);
    };

    return (
      <tr
        className={`sketch-block-row ${isDragging ? "sketch-block-row-dragging" : ""}`}
        onDragOver={isEditable ? (e) => onDragOver?.(e, blockIndex) : undefined}
        onDrop={isEditable ? (e) => onDrop?.(e, blockIndex) : undefined}
      >
        <td className="sketch-block-drag-cell">
          {isEditable && (
            <span
              className="sketch-block-drag-handle"
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData("text/plain", String(block.id));
                e.dataTransfer.effectAllowed = "move";
                onDragStart?.(e, block.id);
              }}
              onDragEnd={() => onDragEnd?.()}
              title="Перетащите для изменения порядка"
            >
              ⋮⋮
            </span>
          )}
        </td>
        <td className="sketch-block-spot-cell">
          <div
            ref={spotDropdownRef}
            className="sketch-block-spot-cell-wrap"
          >
            <span
              role="button"
              tabIndex={isEditable ? 0 : -1}
              className={
                spotName
                  ? "sketch-block-spot-name"
                  : "sketch-block-spot-none"
              }
              style={{
                cursor: isEditable ? "pointer" : "default",
                textDecoration: isEditable ? "underline" : "none",
                textDecorationStyle: isEditable ? "dotted" : "solid",
              }}
              onClick={() => isEditable && setSpotDropdownOpen((v) => !v)}
              onKeyDown={(e) => {
                if (isEditable && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  setSpotDropdownOpen((v) => !v);
                }
              }}
            >
              {spotName ?? "— Без пятна"}
            </span>
            {spotDropdownOpen && isEditable && (
              <div className="sketch-block-spot-dropdown">
                <button
                  type="button"
                  className={`sketch-block-spot-option ${spotId === null ? "active" : ""}`}
                  onClick={() => handleSelectSpot(null)}
                >
                  — Без пятна
                </button>
                {spots.map((spot) => (
                  <button
                    key={spot.id}
                    type="button"
                    className={`sketch-block-spot-option ${spotId === spot.id ? "active" : ""}`}
                    onClick={() => handleSelectSpot(spot)}
                  >
                    {spot.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </td>
        <td>
          <div className="sketch-block-name-cell">
            <div className="sketch-block-icon-wrap">
              <img
                src={VectorIcon}
                alt="block icon"
                className="sketch-block-icon"
              />
            </div>
            <span className="sketch-block-name">{block.name}</span>
          </div>
        </td>
        <td className="sketch-block-area">
          <InlineEditableCell
            ref={areaRef}
            value={block.area || 0}
            isEditable={isEditable}
            onSave={(newValue) => onUpdateBlock(block.id, { area: newValue })}
            onTabNext={() => rateRef.current?.startEditing()}
            width={120}
            step="1"
          />
        </td>
        <td className="sketch-block-rate">
          <InlineEditableCell
            ref={rateRef}
            value={block.rate || 0}
            isEditable={isEditable}
            onSave={(newValue) => onUpdateBlock(block.id, { rate: newValue })}
            onTabNext={() => coeffRef.current?.startEditing()}
            width={120}
            step="1"
          />
        </td>
        <td className="sketch-block-coeff">
          <InlineEditableCell
            ref={coeffRef}
            value={block.attachment_coeff || 0}
            isEditable={isEditable}
            onSave={(newValue) =>
              onUpdateBlock(block.id, { attachment_coeff: newValue })
            }
            onTabNext={onTabToNextRow}
            width={120}
            step="0.1"
          />
        </td>
        <td className="sketch-block-actions">
          <button
            disabled={!isEditable}
            onClick={() => onDeleteBlock(block)}
            className="sketch-delete-btn"
            title="Удалить"
            tabIndex={-1}
          >
            <img src={TrashIcon} alt="Удалить" className="sketch-delete-icon" />
          </button>
        </td>
      </tr>
    );
  },
);

const getApprovalLockMessage = (
  estimateOnApproval,
  commercialOfferOnApproval,
) => {
  if (estimateOnApproval && commercialOfferOnApproval) {
    return "Редактирование запрещено, так как смета и коммерческое предложение находятся на этапе согласования";
  }
  if (estimateOnApproval) {
    return "Редактирование запрещено, так как смета находится на этапе согласования";
  }
  if (commercialOfferOnApproval) {
    return "Редактирование запрещено, так как коммерческое предложение находится на этапе согласования";
  }
  return null;
};

const InitialData = ({
  projectId,
  projectLockedByApproval = false,
  estimateOnApproval = false,
  commercialOfferOnApproval = false,
}) => {
  const isEditable = !projectLockedByApproval;
  const lockBannerMessage = getApprovalLockMessage(
    estimateOnApproval,
    commercialOfferOnApproval,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [category, setCategory] = useState(null);
  const [plotArea, setPlotArea] = useState(0);
  const [plotAreaInputValue, setPlotAreaInputValue] = useState("0");
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [showAddBlockModal, setShowAddBlockModal] = useState(false);
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryRate, setNewCategoryRate] = useState(0);
  const [categoryError, setCategoryError] = useState("");
  const [isCreatingCategoryLoading, setIsCreatingCategoryLoading] =
    useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [spots, setSpots] = useState([]);
  const [showAddSpotModal, setShowAddSpotModal] = useState(false);
  const [editingSpotId, setEditingSpotId] = useState(null);
  const [spotNameInput, setSpotNameInput] = useState("");
  const [spotNameError, setSpotNameError] = useState("");
  const [isSavingSpot, setIsSavingSpot] = useState(false);
  const blockRowRefs = useRef({});
  const [draggedBlockId, setDraggedBlockId] = useState(null);

  // Загружаем категории проекта
  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      const categoriesData = await getSketchDesignProjectCategories();
      const categoriesOptions = [
        CREATE_NEW_CATEGORY_OPTION,
        ...categoriesData.map((cat) => ({
          value: cat.id,
          label: cat.name,
          proposeRate: cat.propose_rate,
        })),
      ];
      setCategories(categoriesOptions);
    } catch (err) {
      console.error("Ошибка при загрузке категорий проекта:", err);
    } finally {
      setLoadingCategories(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!projectId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Получаем данные проекта
        const project = await getSketchDesignProjectById(projectId);
        setProjectData(project);

        // Устанавливаем категорию из селекта (нужно будет загрузить категории сначала)
        // Пока устанавливаем null, после загрузки категорий обновим
        const area = project.full_place_area || 0;
        setPlotArea(area);
        setPlotAreaInputValue(area === 0 ? "" : area.toString());

        // Получаем блоки и пятна проекта
        const [projectBlocks, projectSpots] = await Promise.all([
          getSketchDesignBlocks(projectId),
          getSketchDesignSpots(projectId).catch(() => []),
        ]);
        const sortedBlocks = (projectBlocks || []).sort(
          (a, b) =>
            (a.order_index ?? Number.MAX_SAFE_INTEGER) -
            (b.order_index ?? Number.MAX_SAFE_INTEGER),
        );
        setBlocks(sortedBlocks);
        setSpots(projectSpots || []);
      } catch (err) {
        console.error("Ошибка при загрузке данных проекта ЭП:", err);
        setError("Не удалось загрузить данные проекта");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId]);

  // Обновляем категорию после загрузки категорий и данных проекта
  useEffect(() => {
    const categoryName =
      projectData?.sketch_design_category ||
      projectData?.sketch_desing_category; // поддержка старого поля для обратной совместимости
    if (categoryName && categories.length > 0) {
      const categoryOption = categories.find(
        (cat) => cat.label === categoryName,
      );
      if (categoryOption) {
        setCategory((prevCategory) =>
          prevCategory?.value === categoryOption.value
            ? prevCategory
            : categoryOption,
        );
      }
    }
  }, [projectData, categories]);

  if (loading) {
    return <Preloader />;
  }

  if (error) {
    return <div style={{ padding: 24, color: "red" }}>{error}</div>;
  }

  const totalArea = blocks.reduce((sum, block) => sum + (block.area || 0), 0);
  const blocksCount = blocks.filter((b) => b.is_active).length;

  return (
    <div className="sketch-design-initial-data">
      {projectLockedByApproval && lockBannerMessage && (
        <div className="sketch-initial-data-lock-banner" role="alert">
          <span className="sketch-initial-data-lock-banner-icon">⚠</span>
          {lockBannerMessage}
        </div>
      )}
      <div className="initial-data-content">
        <div className="main-section">
          <h2 className="sketch-section-title">
            Исходные технико-экономические показатели
          </h2>

          <div className="project-inputs">
            <div className="input-group">
              <label>Тип здания</label>
              <div className="building-type-display">
                {projectData?.building_type || "Не указано"}
              </div>
            </div>
            <div className="input-group">
              <label>Категория объекта</label>
              <Select
                styles={selectStyles}
                options={categories}
                value={category}
                isDisabled={!isEditable}
                onChange={(selectedOption) => {
                  if (
                    selectedOption?.value === CREATE_NEW_CATEGORY_OPTION.value
                  ) {
                    setIsCreatingCategory(true);
                    setNewCategoryName("");
                    setNewCategoryRate(0);
                    setCategoryError("");
                    setCategory(null);
                  } else {
                    setIsCreatingCategory(false);
                    setCategory(selectedOption);
                    setNewCategoryName("");
                    setNewCategoryRate(0);
                    setCategoryError("");

                    // Автоматически подставляем рекомендованную ставку из категории во все блоки
                    if (
                      selectedOption?.proposeRate !== undefined &&
                      selectedOption.proposeRate > 0
                    ) {
                      const rate = selectedOption.proposeRate;
                      setBlocks((prevBlocks) =>
                        prevBlocks.map((block) => ({
                          ...block,
                          rate: rate,
                        })),
                      );
                    }
                  }
                }}
                placeholder="Выберите категорию..."
                isLoading={loadingCategories}
                isClearable
                noOptionsMessage={() => "Нет доступных категорий"}
              />
              {isCreatingCategory && (
                <div className="create-category-form" style={{ marginTop: 12 }}>
                  <div style={{ marginBottom: 12 }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: 6,
                        fontSize: 14,
                      }}
                    >
                      Название категории*
                    </label>
                    <div style={{ position: "relative" }}>
                      <input
                        type="text"
                        value={newCategoryName}
                        onChange={(e) => {
                          const value = e.target.value;
                          setNewCategoryName(value);
                          setCategoryError("");
                          // Проверяем уникальность
                          if (value.trim()) {
                            const existingCategory = categories.find(
                              (cat) =>
                                cat.value !==
                                  CREATE_NEW_CATEGORY_OPTION.value &&
                                cat.label.toLowerCase().trim() ===
                                  value.toLowerCase().trim(),
                            );
                            if (existingCategory) {
                              setCategoryError(
                                "Такая категория уже существует! Выберите её из списка.",
                              );
                            }
                          }
                        }}
                        placeholder="Введите название категории..."
                        className={`plot-area-input ${
                          categoryError ? "error" : ""
                        }`}
                        style={{
                          width: "100%",
                          borderColor: categoryError ? "#ff4444" : undefined,
                        }}
                        autoFocus
                      />
                      {newCategoryName.trim() && !categoryError && (
                        <span
                          style={{
                            position: "absolute",
                            right: 12,
                            top: "50%",
                            transform: "translateY(-50%)",
                            color: "#2A5E38",
                            fontSize: 18,
                          }}
                        >
                          ✓
                        </span>
                      )}
                    </div>
                    {categoryError && (
                      <div
                        style={{
                          color: "#ff4444",
                          fontSize: 12,
                          marginTop: 4,
                        }}
                      >
                        {categoryError}
                      </div>
                    )}
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: 6,
                        fontSize: 14,
                      }}
                    >
                      Рекомендованная ставка (тг/м²)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newCategoryRate}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        setNewCategoryRate(value);
                      }}
                      placeholder="0"
                      className="plot-area-input"
                      style={{ width: "100%" }}
                    />
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      type="button"
                      onClick={async () => {
                        if (!newCategoryName.trim()) {
                          setCategoryError("Введите название категории");
                          return;
                        }
                        if (categoryError) {
                          return;
                        }

                        setIsCreatingCategoryLoading(true);
                        try {
                          const newCategory =
                            await addSketchDesignProjectCategory(
                              newCategoryName.trim(),
                              newCategoryRate,
                            );
                          // Обновляем список категорий
                          await loadCategories();
                          // Выбираем созданную категорию
                          const createdCategoryOption = {
                            value: newCategory.id,
                            label: newCategory.name,
                            proposeRate: newCategory.propose_rate,
                          };
                          setCategory(createdCategoryOption);
                          setIsCreatingCategory(false);
                          setNewCategoryName("");
                          setNewCategoryRate(0);
                          setCategoryError("");

                          // Автоматически подставляем рекомендованную ставку из созданной категории во все блоки
                          if (
                            newCategory.propose_rate !== undefined &&
                            newCategory.propose_rate > 0
                          ) {
                            const rate = newCategory.propose_rate;
                            setBlocks((prevBlocks) =>
                              prevBlocks.map((block) => ({
                                ...block,
                                rate: rate,
                              })),
                            );
                          }
                        } catch (err) {
                          console.error("Ошибка при создании категории:", err);
                          if (
                            err.message.includes("уже существует") ||
                            err.message.includes("already exists")
                          ) {
                            setCategoryError(
                              "Такая категория уже существует! Выберите её из списка.",
                            );
                          } else {
                            setCategoryError(
                              "Ошибка при создании категории. Попробуйте еще раз.",
                            );
                          }
                        } finally {
                          setIsCreatingCategoryLoading(false);
                        }
                      }}
                      className="save-btn"
                      style={{
                        padding: "8px 16px",
                        fontSize: 14,
                        minWidth: 120,
                      }}
                      disabled={
                        !isEditable ||
                        !newCategoryName.trim() ||
                        !!categoryError ||
                        isCreatingCategoryLoading
                      }
                    >
                      {isCreatingCategoryLoading ? "Создание..." : "Создать"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsCreatingCategory(false);
                        setNewCategoryName("");
                        setNewCategoryRate(0);
                        setCategoryError("");
                        setCategory(null);
                      }}
                      style={{
                        padding: "8px 16px",
                        fontSize: 14,
                        backgroundColor: "white",
                        color: "#666",
                        border: "1px solid #ddd",
                        borderRadius: 6,
                        cursor: "pointer",
                      }}
                      disabled={isCreatingCategoryLoading}
                    >
                      Отмена
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="input-group sketch-spots-group">
              <label>Пятна</label>
              <div className="sketch-spots-list">
                {spots.length === 0 ? (
                  <span className="sketch-spots-empty">
                    Пятна не созданы. Добавьте пятно, чтобы группировать блоки.
                  </span>
                ) : (
                  spots.map((spot) => (
                    <div key={spot.id} className="sketch-spot-chip">
                      <span className="sketch-spot-chip-name">{spot.name}</span>
                      {isEditable && (
                        <>
                          <button
                            type="button"
                            className="sketch-spot-chip-btn"
                            onClick={() => {
                              setEditingSpotId(spot.id);
                              setSpotNameInput(spot.name);
                              setSpotNameError("");
                              setShowAddSpotModal(true);
                            }}
                            title="Изменить"
                          >
                            ✎
                          </button>
                          <button
                            type="button"
                            className="sketch-spot-chip-btn sketch-spot-chip-btn-delete"
                            onClick={async () => {
                              if (
                                !window.confirm(
                                  `Удалить пятно «${spot.name}»? Блоки останутся без привязки.`,
                                )
                              )
                                return;
                              try {
                                await deleteSketchDesignSpot(spot.id);
                                // Удаляем пятно из локального состояния
                                setSpots((prev) =>
                                  prev.filter((s) => s.id !== spot.id),
                                );
                                // И одновременно убираем из состояния все блоки, которые были привязаны к этому пятну,
                                // чтобы при сохранении не отправлять обновления по уже удалённым на бэке блокам
                                setBlocks((prevBlocks) =>
                                  prevBlocks.filter(
                                    (b) =>
                                      (b.spot_id ?? (b.spot && b.spot.id)) !==
                                      spot.id,
                                  ),
                                );
                              } catch (err) {
                                console.error(err);
                                setSuccessMessage("Ошибка при удалении пятна");
                                setShowSuccessPopup(true);
                                setTimeout(
                                  () => setShowSuccessPopup(false),
                                  3000,
                                );
                              }
                            }}
                            title="Удалить"
                          >
                            ×
                          </button>
                        </>
                      )}
                    </div>
                  ))
                )}
                {isEditable && (
                  <button
                    type="button"
                    className="sketch-add-spot-btn"
                    onClick={() => {
                      setEditingSpotId(null);
                      setSpotNameInput("");
                      setSpotNameError("");
                      setShowAddSpotModal(true);
                    }}
                  >
                    + Добавить пятно
                  </button>
                )}
              </div>
            </div>
            <div className="input-group">
              <label>Введите площадь участка* (га)</label>
              <input
                type="number"
                step="0.001"
                min="0"
                value={plotAreaInputValue}
                readOnly={!isEditable}
                onChange={(e) => {
                  if (!isEditable) return;
                  const inputValue = e.target.value;
                  // Если поле пустое, устанавливаем пустую строку
                  if (inputValue === "" || inputValue === "-") {
                    setPlotAreaInputValue("");
                    setPlotArea(0);
                    return;
                  }
                  // Парсим значение
                  const numValue = parseFloat(inputValue);
                  if (!isNaN(numValue) && numValue >= 0) {
                    setPlotAreaInputValue(inputValue);
                    setPlotArea(numValue);
                  }
                }}
                onFocus={(e) => {
                  // При фокусе, если значение 0, очищаем поле
                  if (plotArea === 0) {
                    setPlotAreaInputValue("");
                    const input = e.target;
                    requestAnimationFrame(() => input.select());
                  }
                }}
                onBlur={(e) => {
                  if (!isEditable) return;
                  // При потере фокуса, если поле пустое, устанавливаем 0
                  if (e.target.value === "" || e.target.value === "-") {
                    setPlotAreaInputValue("0");
                    setPlotArea(0);
                  } else {
                    // Форматируем значение при потере фокуса
                    const numValue = parseFloat(e.target.value);
                    if (!isNaN(numValue) && numValue >= 0) {
                      setPlotAreaInputValue(numValue.toString());
                      setPlotArea(numValue);
                    }
                  }
                }}
                placeholder="0"
                className="plot-area-input"
              />
            </div>
          </div>

          <div className="blocks-table">
            <table>
              <thead>
                <tr>
                  <th className="sketch-block-drag-cell" />
                  <th>Пятно</th>
                  <th>Наименование объекта проектирования</th>
                  <th>Площадь (м²)</th>
                  <th>Ставка (тг/м²)</th>
                  <th>Коэффициент привязки</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {blocks.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      style={{ textAlign: "center", padding: 24 }}
                    >
                      Нет данных о блоках
                    </td>
                  </tr>
                ) : (
                  (() => {
                    // 0 и null = «без пятна»; бэкенд может вернуть spot_id: 0 при сбросе
                    const getBlockSpotId = (b) => {
                      const id = b.spot_id ?? b.spot?.id ?? null;
                      return id === 0 ? null : id;
                    };
                    const getBlockSpotName = (b) =>
                      b.spot?.name != null
                        ? b.spot.name
                        : (spots.find((s) => s.id === getBlockSpotId(b))
                            ?.name ?? null);
                    const handleDragStart = (e, blockId) => {
                      setDraggedBlockId(blockId);
                    };
                    const handleDragOver = (e, dropIndex) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                    };
                    const handleDrop = (e, dropIndex) => {
                      e.preventDefault();
                      setDraggedBlockId(null);
                      const blockId = Number(e.dataTransfer.getData("text/plain"));
                      if (!blockId) return;
                      const fromIndex = blocks.findIndex((b) => b.id === blockId);
                      if (fromIndex === -1 || fromIndex === dropIndex) return;
                      const newBlocks = [...blocks];
                      const [removed] = newBlocks.splice(fromIndex, 1);
                      newBlocks.splice(dropIndex, 0, removed);
                      setBlocks(newBlocks);
                    };
                    return blocks.map((block, index) => (
                      <BlockRow
                        key={block.id}
                        ref={(el) => {
                          blockRowRefs.current[block.id] = el;
                        }}
                        block={block}
                        blockIndex={index}
                        spotName={getBlockSpotName(block)}
                        spotId={getBlockSpotId(block)}
                        spots={spots}
                        isEditable={isEditable}
                        onDragStart={handleDragStart}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        onDragEnd={() => setDraggedBlockId(null)}
                        isDragging={draggedBlockId === block.id}
                        onUpdateBlock={(blockId, updates) => {
                          setBlocks((prevBlocks) =>
                            prevBlocks.map((b) =>
                              b.id === blockId ? { ...b, ...updates } : b,
                            ),
                          );
                        }}
                        onDeleteBlock={async (blockToDelete) => {
                          if (!isEditable) return;
                          if (
                            window.confirm(
                              `Вы уверены, что хотите удалить блок "${blockToDelete.name}"?`,
                            )
                          ) {
                            try {
                              await deleteSketchDesignBlock(blockToDelete.id);
                              setBlocks((prevBlocks) =>
                                prevBlocks.filter(
                                  (b) => b.id !== blockToDelete.id,
                                ),
                              );
                            } catch (err) {
                              console.error("Ошибка при удалении блока:", err);
                              setSuccessMessage(
                                "Ошибка при удалении блока. Попробуйте еще раз.",
                              );
                              setShowSuccessPopup(true);
                              setTimeout(
                                () => setShowSuccessPopup(false),
                                3000,
                              );
                            }
                          }
                        }}
                        onTabToNextRow={() => {
                          const nextBlock = blocks[index + 1];
                          if (nextBlock) {
                            blockRowRefs.current[
                              nextBlock.id
                            ]?.startEditingArea();
                          }
                        }}
                      />
                    ));
                  })()
                )}
              </tbody>
            </table>
            <button
              type="button"
              className="add-block-btn"
              onClick={() => setShowAddBlockModal(true)}
              disabled={!isEditable}
            >
              + Добавить блок
            </button>
          </div>
        </div>

        <div className="sketch-summary-panel">
          <div className="sketch-summary-item sketch-summary-item-top">
            <img src={PyramidIcon} alt="plot" className="sketch-summary-icon" />
            <div className="sketch-summary-row">
              <div className="sketch-summary-label">Площадь участка:</div>
              <div className="sketch-summary-value">
                {plotArea > 0 ? `${plotArea.toFixed(3)} га` : "-"}
              </div>
            </div>
          </div>
          <div className="sketch-summary-item">
            <img
              src={BuildingIcon}
              alt="blocks"
              className="sketch-summary-icon sketch-summary-icon-small"
            />
            <div className="sketch-summary-row">
              <div className="sketch-summary-label">Количество блоков:</div>
              <div className="sketch-summary-value">{blocksCount}</div>
            </div>
          </div>
          <div className="sketch-summary-item">
            <img
              src={CubesIcon}
              alt="area"
              className="sketch-summary-icon sketch-summary-icon-small"
            />
            <div className="sketch-summary-row">
              <div className="sketch-summary-label">Общая площадь</div>
              <div className="sketch-summary-value">
                {totalArea.toLocaleString()} м²
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="save-section">
        <button
          className="save-btn"
          disabled={!isEditable || isSaving}
          onClick={async () => {
            if (!isEditable || isSaving) return;
            setIsSaving(true);
            try {
              // Сначала сохраняем данные проекта (категория и площадь участка)
              try {
                await updateSketchDesignProject(
                  projectId,
                  category?.label || null,
                  plotArea || 0,
                );
              } catch (projectErr) {
                console.error(
                  "Ошибка при сохранении данных проекта:",
                  projectErr,
                );
                // Продолжаем сохранение блоков даже если проект не сохранился
              }

              // Сохраняем все блоки
              let blocksSaved = false;
              try {
                await saveSketchDesignBlocks(projectId, blocks);
                blocksSaved = true;
              } catch (saveError) {
                // Если массовый эндпоинт не работает, пробуем обновить каждый блок отдельно
                console.log(
                  "Массовый эндпоинт не найден, пробуем обновить блоки по одному",
                );
                let successCount = 0;
                let errorCount = 0;

                for (let i = 0; i < blocks.length; i++) {
                  const block = blocks[i];
                  try {
                    const payload = {
                      area: block.area || 0,
                      rate: block.rate || 0,
                      attachment_coeff: block.attachment_coeff || 0,
                      order_index: i,
                    };
                    if (block.spot_id !== undefined)
                      payload.spot_id = block.spot_id;
                    await updateSketchDesignBlock(block.id, payload);
                    successCount++;
                  } catch (err) {
                    console.error(
                      `Ошибка при обновлении блока ${block.id}:`,
                      err,
                    );
                    errorCount++;
                  }
                }

                if (errorCount === 0) {
                  blocksSaved = true;
                } else {
                  setSuccessMessage(
                    `Сохранено блоков: ${successCount}, ошибок: ${errorCount}. Проверьте консоль для деталей.`,
                  );
                  setShowSuccessPopup(true);
                  setTimeout(() => setShowSuccessPopup(false), 4000);
                  setIsSaving(false);
                  return;
                }
              }

              if (blocksSaved) {
                setSuccessMessage("Данные успешно сохранены!");
                setShowSuccessPopup(true);
                setTimeout(() => setShowSuccessPopup(false), 3000);
              }
            } catch (err) {
              console.error("Ошибка при сохранении:", err);
              setSuccessMessage(
                "Ошибка при сохранении данных. Проверьте консоль для деталей.",
              );
              setShowSuccessPopup(true);
              setTimeout(() => setShowSuccessPopup(false), 4000);
            } finally {
              setIsSaving(false);
            }
          }}
        >
          {isSaving ? "Сохранение..." : "Сохранить"}
        </button>
      </div>

      {showAddSpotModal && (
        <>
          <div
            className="sketch-add-block-modal-backdrop"
            onClick={() => {
              setShowAddSpotModal(false);
              setEditingSpotId(null);
              setSpotNameError("");
            }}
          />
          <div className="sketch-add-block-modal sketch-spot-modal">
            <div className="sketch-add-block-modal-title">
              {editingSpotId ? "Редактировать пятно" : "Добавить пятно"}
            </div>
            <div className="sketch-add-block-modal-content">
              <div className="sketch-add-block-form-group">
                <label className="sketch-add-block-label">
                  Название пятна*
                </label>
                <input
                  type="text"
                  value={spotNameInput}
                  onChange={(e) => {
                    setSpotNameInput(e.target.value);
                    setSpotNameError("");
                  }}
                  placeholder="Например: Корпус А"
                  className={`sketch-add-block-input sketch-add-spot-input ${spotNameError ? "error" : ""}`}
                  autoFocus
                />
                {spotNameError && (
                  <div className="sketch-add-block-error">{spotNameError}</div>
                )}
              </div>
            </div>
            <div className="sketch-add-block-modal-actions">
              <button
                type="button"
                className="sketch-add-block-btn-cancel"
                disabled={isSavingSpot}
                onClick={() => {
                  setShowAddSpotModal(false);
                  setEditingSpotId(null);
                  setSpotNameError("");
                }}
              >
                Отмена
              </button>
              <button
                type="button"
                className="sketch-add-block-btn-submit"
                disabled={!spotNameInput.trim() || isSavingSpot}
                onClick={async () => {
                  const name = spotNameInput.trim();
                  if (!name) return;
                  const existing = spots.find(
                    (s) =>
                      s.name &&
                      s.name.toLowerCase() === name.toLowerCase() &&
                      s.id !== editingSpotId,
                  );
                  if (existing) {
                    setSpotNameError("Пятно с таким названием уже существует");
                    return;
                  }
                  setIsSavingSpot(true);
                  setSpotNameError("");
                  try {
                    if (editingSpotId) {
                      const updated = await updateSketchDesignSpot(
                        editingSpotId,
                        { name },
                      );
                      setSpots((prev) =>
                        prev.map((s) => (s.id === updated.id ? updated : s)),
                      );
                    } else {
                      const created = await createSketchDesignSpot(projectId, {
                        name,
                      });
                      setSpots((prev) => [...prev, created]);
                    }
                    setShowAddSpotModal(false);
                    setEditingSpotId(null);
                    setSpotNameInput("");
                  } catch (err) {
                    console.error(err);
                    setSpotNameError(
                      "Ошибка при сохранении. Попробуйте еще раз.",
                    );
                  } finally {
                    setIsSavingSpot(false);
                  }
                }}
              >
                {isSavingSpot
                  ? "Сохранение..."
                  : editingSpotId
                    ? "Сохранить"
                    : "Добавить"}
              </button>
            </div>
          </div>
        </>
      )}

      <AddBlockModal
        open={showAddBlockModal}
        onClose={() => setShowAddBlockModal(false)}
        projectId={projectId}
        spots={spots}
        blocks={blocks}
        onBlockAdded={(addedBlocks) => {
          if (addedBlocks && addedBlocks.length > 0) {
            setBlocks((prevBlocks) => {
              const startOrder = prevBlocks.length;
              const withOrder = addedBlocks.map((b, i) => ({
                ...b,
                order_index: startOrder + i,
              }));
              return [...prevBlocks, ...withOrder];
            });
          }
          setShowAddBlockModal(false);
        }}
      />

      {showSuccessPopup && (
        <div
          className="popup-overlay"
          onClick={() => setShowSuccessPopup(false)}
        >
          <div className="popup">
            <div className="popup-icon"></div>
            <p className="popup-text">{successMessage}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default InitialData;
